import * as React from 'react';
import { useDraggable } from '@dnd-kit/core';
import { Bolt, MapPin, User } from 'lucide-react';
import { Avatar } from './avatar.js';
import { dueKind, daysUntilDue, formatDueDate, taskOriginKey } from '../lib/task-board.js';
import type { Task } from '../lib/tasks-api.js';
import { useT } from '../lib/i18n.js';

/**
 * Krok TAB1 bod 5 — KARTA ÚKOLU podle predlohy
 * `docs/design/ux-v3/tabule/Ukoly.png`:
 *
 *   titulek · stitek zakazky s ikonou mista (odkaz na detail) · zdroj
 *   („automaticky · prověření" / „kalendář" / „ruční") · termin (cervene
 *   „po termínu") · avatar resitele · JEDNA kontextova akce.
 *
 * Stejne pravidlo jako `kanban-card.tsx` a `demand-card.tsx`: presun je v DOM
 * zakazany, kdyz `canUpdate=false` — makler bez prava kartu nezvedne, misto
 * aby teprve dostal 403. Povolene cile presunu NEURCUJE tato karta (to je
 * `allowedTransitions` z API) — sloupec si je prebira z payloadu karty.
 */
export interface TaskCardProps {
  readonly item: Task;
  readonly now: Date;
  readonly onOpen: (id: string) => void;
  /** Nazev zakazky, ke ktere ukol patri; `null` = ukol zakazku nema. */
  readonly caseName: string | null;
  /** Jmeno resitele pro avatar; `null` = ukol nema maklere. */
  readonly ownerName: string | null;
  /**
   * JEDNA kontextova akce karty (zadani bod 5). `null` = karta zadnou nema
   * (hotove ukoly). Vykresleni je tady, VYZNAM urcuje tabule — karta nesmi
   * rozhodovat o tom, co se s ukolem da delat.
   */
  readonly action: { readonly label: string; readonly onAction: () => void } | null;
  /** Odkaz na detail zakazky (stitek s ikonou mista). */
  readonly onOpenCase: (() => void) | null;
  /** Hotovy ukol je v predloze PRESKRTNUTY a ztlumeny. */
  readonly done: boolean;
}

export function TaskCard({
  item,
  now,
  onOpen,
  caseName,
  ownerName,
  action,
  onOpenCase,
  done,
}: TaskCardProps): React.JSX.Element {
  const t = useT();
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: item.id,
    disabled: !item.canUpdate,
    data: { status: item.status, allowedTransitions: item.allowedTransitions },
  });

  const style: React.CSSProperties | undefined = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
        zIndex: isDragging ? 10 : undefined,
        opacity: isDragging ? 0.6 : undefined,
      }
    : undefined;

  const kind = dueKind(item.dueAt, now);
  const days = daysUntilDue(item.dueAt, now);
  const origin = taskOriginKey(item.originKind);

  /*
   * Zdroj ukolu — krok UXF2 bod 3 (nalez #33): na karte stoji JEDNIM SLOVEM
   * („automaticky" / „ruční"). Do UXF2 nesla karta i KONKRETNI zdroj
   * („automaticky · prověření"), takze radka zdroje prerustala ostatni udaje
   * a karta byla vyssi; konkretni zdroj je v detailu ukolu (`task-drawer`,
   * `task-drawer-origin-kind`) a tady zustava v `title` pro mys a ctecku.
   */
  const sourceText = origin === null ? t('webAdmin.tasks.sourceManual') : t('webAdmin.tasks.sourceAuto');
  const sourceTitle =
    origin === null
      ? undefined
      : t(`webAdmin.tasks.origin.${origin}` as 'webAdmin.tasks.origin.case_control');

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`rh-a2-tcard${done ? ' rh-a2-tcard--done' : ''}${item.canUpdate ? '' : ' rh-a2-tcard--locked'}`}
      data-testid={`task-card-${item.id}`}
      data-status={item.status}
      role="button"
      tabIndex={0}
      onClick={() => onOpen(item.id)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') onOpen(item.id);
      }}
      title={item.canUpdate ? undefined : t('webAdmin.tasks.noMovePermissionHint')}
    >
      <div className="rh-a2-tcard__title">{done ? <s>{item.title}</s> : item.title}</div>

      {caseName !== null || (origin !== null && item.originLabel !== null && item.originLabel !== '') ? (
        <div className="rh-a2-tcard__row">
          {/*
            Stitek zakazky je ODKAZ na detail (zadani bod 5). `stopPropagation`
            je nutne: cela karta otevira drawer ukolu, takze bez nej by klik na
            stitek otevrel oboji.
          */}
          {caseName !== null ? (
            <button
              type="button"
              className="rh-a2-tag rh-a2-tag--case"
              data-testid={`task-case-${item.id}`}
              disabled={onOpenCase === null}
              title={onOpenCase === null ? undefined : t('webAdmin.tasks.openCase')}
              onClick={(e) => {
                e.stopPropagation();
                onOpenCase?.();
              }}
            >
              <MapPin size={11} strokeWidth={2} aria-hidden="true" />
              <span>{caseName}</span>
            </button>
          ) : null}
          {/*
            ODKAZ NA ZDROJ zustava na karte (krok CRM2: „automaticky vzniklý
            úkol bez odkazu na zdroj je jen brekot") — cislo faktury, kod brany.
            Predloha ma v teto rade vedle stitku zakazky prave takove doplnujici
            stitky („nový kontakt", „příloha PDF").
          */}
          {origin !== null && item.originLabel !== null && item.originLabel !== '' ? (
            <span className="rh-a2-tag rh-a2-tag--non" data-testid={`task-origin-label-${item.id}`}>
              {item.originLabel}
            </span>
          ) : null}
        </div>
      ) : null}

      <div className="rh-a2-tcard__foot">
        <span
          className="rh-a2-tcard__src"
          data-testid={`task-origin-${item.id}`}
          {...(sourceTitle === undefined ? {} : { title: sourceTitle })}
        >
          <span className="rh-a2-ico rh-a2-ico--sm" aria-hidden="true">
            {origin === null ? <User size={12} strokeWidth={2} /> : <Bolt size={12} strokeWidth={2} />}
          </span>
          {sourceText}
        </span>
        <span className="rh-a2-tcard__due">
          {kind === 'none' ? (
            <span data-testid={`task-due-${item.id}`}>{t('webAdmin.tasks.noDue')}</span>
          ) : (
            <span
              data-testid={`task-due-${item.id}`}
              data-overdue={String(kind === 'overdue')}
              className={kind === 'overdue' ? 'rh-a2-tcard__due--overdue' : undefined}
            >
              {formatDueDate(item.dueAt)}
              {kind === 'overdue' ? ` · ${t('webAdmin.tasks.overdue')}` : ''}
              {kind === 'later' && days !== null && days > 1
                ? ` · ${t('webAdmin.tasks.dueInDays', { days: String(days) })}`
                : ''}
            </span>
          )}
        </span>
        {ownerName !== null ? (
          <Avatar name={ownerName} />
        ) : (
          <span className="rh-a2-meta" data-testid={`task-no-owner-${item.id}`}>
            {t('webAdmin.tasks.noOwner')}
          </span>
        )}
      </div>

      {action !== null ? (
        <div className="rh-a2-tcard__act">
          <button
            type="button"
            className="rh-a2-btn rh-a2-btn--sm"
            data-testid={`task-action-${item.id}`}
            onClick={(e) => {
              e.stopPropagation();
              action.onAction();
            }}
          >
            {action.label}
          </button>
        </div>
      ) : null}
    </div>
  );
}
